import { useCallback, useState } from "react";
import { toast } from "sonner";
import { buildUpsellExport, type UpsellExportLine } from "@/lib/upsell-export";

interface CopyableUpsell {
  description: string;
  overhead_pct: number | null;
  discount_pct: number | null;
  discount_amount: number | null;
  lines: UpsellExportLine[];
}

/** Skriv rik tekst (HTML) + ren tekst-fallback til utklippstavla. */
async function writeRichText(html: string, text: string): Promise<void> {
  const clip = navigator.clipboard;
  if (clip && "write" in clip && typeof ClipboardItem !== "undefined") {
    await clip.write([
      new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([text], { type: "text/plain" }),
      }),
    ]);
    return;
  }
  // Eldre nettlesere: kun ren tekst.
  if (clip?.writeText) {
    await clip.writeText(text);
    return;
  }
  throw new Error("Utklippstavle ikke tilgjengelig");
}

/**
 * VIB-951: kopier et mersalg som mail-vennlig tilbud (rik tekst + ren tekst).
 * Kunde-vendt: kun beskrivelse/mengde/sum, ingen kost/margin.
 */
export function useCopyUpsell() {
  const [copying, setCopying] = useState(false);

  const copyUpsell = useCallback(
    async (upsell: CopyableUpsell, defaultOverheadPct: number) => {
      setCopying(true);
      try {
        const { html, text } = buildUpsellExport({
          title: upsell.description,
          lines: upsell.lines,
          overhead_pct: upsell.overhead_pct,
          discount_pct: upsell.discount_pct,
          discount_amount: upsell.discount_amount,
          defaultOverheadPct,
        });
        await writeRichText(html, text);
        toast.success("Kopiert — lim inn i e-posten til kunden");
      } catch (err) {
        toast.error(`Kunne ikke kopiere: ${(err as Error).message}`);
      } finally {
        setCopying(false);
      }
    },
    [],
  );

  return { copyUpsell, copying };
}
